import type { PetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { SECTION_ID_MAPPING } from '#src/app/pages/petitionDetails/shared/constants';
import type { Endorsement } from '#src/app/shared/components/endorsements';

import type { HeroActivityType, HeroResult } from './types';
import { parseDate } from './utils';

export type TranslateFn = (key: string, replacements?: Record<string, string | number>) => string;

export type ResolverContext = {
	data: PetitionDetailsPageData;
	translate: TranslateFn;
	latestEndorsement: Endorsement | null;
};

export type HeroResolver = (ctx: ResolverContext) => HeroResult | null;

function stripHtmlAndTrim(value: string): string {
	return value
		.replace(/<[^>]*>/g, ' ')
		.replace(/&nbsp;/gi, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

function hasMeaningfulStarterUpdateContent(update: { title: string; description: string }): boolean {
	const title = update.title.trim();
	const description = stripHtmlAndTrim(update.description);
	return title.length > 0 && description.length > 0;
}

export const heroResolvers: Record<HeroActivityType, HeroResolver> = {
	dmResponse: ({ data, translate }) => {
		const nodes = data.petition.dmResponsesConnection?.nodes;

		if (!nodes || nodes.length === 0) return null;

		const latestDmResponse = nodes[0];
		const dmName = latestDmResponse.decisionMaker.displayName;
		const timeAgo = latestDmResponse.createdAtRelativeLocalized;
		return {
			content: {
				badgeLabel: translate('corgi.petition_details.momentum_bar.spotlight.dm_response_badge'),
				text: translate('corgi.petition_details.momentum_bar.spotlight.dm_response_text', { name: dmName }),
				relativeTime: timeAgo,
				showIcon: false,
				sectionId: SECTION_ID_MAPPING.DECISION_MAKERS,
			},
			timestamp: parseDate(latestDmResponse.createdAt),
		};
	},

	endorsement: ({ translate, latestEndorsement }) => {
		if (!latestEndorsement) return null;

		const endorserName = latestEndorsement.name;
		const timeAgo = latestEndorsement.createdAtRelativeLocalized;
		return {
			content: {
				badgeLabel: translate('corgi.petition_details.momentum_bar.spotlight.endorsement_badge'),
				text: translate('corgi.petition_details.momentum_bar.spotlight.endorsement_text_with_time', {
					name: endorserName,
				}),
				relativeTime: timeAgo,
				showIcon: false,
				sectionId: SECTION_ID_MAPPING.ENDORSEMENTS,
			},
			timestamp: parseDate(latestEndorsement.createdAt),
		};
	},

	mediaMention: ({ data, translate }) => {
		const { mediaSources } = data.mediaSourcesPage;
		if (mediaSources.length === 0) return null;

		const source = mediaSources[0];
		const timeAgo = source.publishedAtRelativeLocalized;
		return {
			content: {
				badgeLabel: translate('corgi.petition_details.momentum_bar.spotlight.media_mention_badge'),
				text: source.name
					? translate('corgi.petition_details.momentum_bar.spotlight.media_mention_text', {
							outletName: source.name,
						})
					: translate('corgi.petition_details.momentum_bar.spotlight.media_mention_text_no_outlet'),
				relativeTime: timeAgo,
				showIcon: false,
				sectionId: SECTION_ID_MAPPING.MEDIA_MENTIONS,
			},
			timestamp: parseDate(source.publishedAt),
		};
	},

	starterUpdate: ({ data, translate }) => {
		const latestStarterUpdate = data.petition.starterPetitionUpdatesConnection.nodes.find(
			(
				update,
			): update is Extract<
				(typeof data.petition.starterPetitionUpdatesConnection.nodes)[number],
				{ __typename: 'StarterPetitionUpdate' }
			> => update.__typename === 'StarterPetitionUpdate' && hasMeaningfulStarterUpdateContent(update),
		);
		if (!latestStarterUpdate) return null;

		return {
			content: {
				badgeLabel: translate('corgi.petition_details.momentum_bar.spotlight.starter_update_badge'),
				text: translate('corgi.petition_details.momentum_bar.spotlight.starter_update_text'),
				relativeTime: latestStarterUpdate.createdAtRelativeLocalized,
				showIcon: false,
				sectionId: SECTION_ID_MAPPING.UPDATES,
			},
			timestamp: parseDate(latestStarterUpdate.createdAt),
		};
	},
};
